import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bell, Phone, PhoneMissed, Voicemail, MessageSquare, MessageCircle,
  AtSign, Mail, Newspaper, Users, AlertTriangle, HardDrive, Calendar,
  CheckSquare, Video, Workflow, FileText, Briefcase, ListTodo, Zap,
  ShieldCheck, Server, Save, RefreshCw, Play, Moon, Music, UserPlus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@easyflow/ui-frontend/components/ui/accordion';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/contexts/PermissionContext';
import { apiClient } from '@/lib/api';
import {
  useNotificationPreferences,
  type CategoryPreference,
  type CategoryUpdate,
  type EmailAccountOverride,
  type NotificationCategory,
  type NotificationPreferencesPayload,
  type QuietHoursPreference,
} from '@/hooks/useNotificationPreferences';
import TelegramPairingCard from './TelegramPairingCard';
import { Send, Smartphone, Monitor } from 'lucide-react';
import { NOTIFICATION_CHANNELS, type NotificationChannelKey } from '@easyflow/shared';

// Existing EasyFlow icon used as graceful fallback if a channel-specific
// notification asset is ever missing (avoids 404 loops in the preview).
const FALLBACK_NOTIFICATION_ICON = '/notifications/notification-icon-192.png';

interface CategoryDef {
  key: NotificationCategory;
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  vehicles: { in_app: boolean; push: boolean; email: boolean; sound: boolean; telegram: boolean };
  reminders?: boolean;
  /** When true, the per-category tone selector is shown for this row. */
  tone?: boolean;
  adminOnly?: boolean;
  /** Riga visibile solo se l'utente ha questo permesso (gli admin bypassano). */
  permission?: string;
}

interface CategoryGroup {
  id: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  categories: CategoryDef[];
}

type VehicleKey = 'push' | 'in_app' | 'email' | 'sound' | 'telegram';
type VehicleEnabledKey =
  | 'push_enabled' | 'in_app_enabled' | 'email_enabled' | 'sound_enabled' | 'telegram_enabled';

interface VehicleDef {
  vk: VehicleKey;
  ek: VehicleEnabledKey;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Single source of truth for the 5 notification vehicles, reused by the master
// toggle bar, the per-domain summary indicators and the column headers.
const VEHICLE_DEFS: readonly VehicleDef[] = [
  { vk: 'push', ek: 'push_enabled', label: 'Push', icon: Smartphone },
  { vk: 'in_app', ek: 'in_app_enabled', label: 'In-app', icon: Bell },
  { vk: 'email', ek: 'email_enabled', label: 'Email', icon: Mail },
  { vk: 'sound', ek: 'sound_enabled', label: 'Suono', icon: Music },
  { vk: 'telegram', ek: 'telegram_enabled', label: 'Telegram', icon: Send },
] as const;

type GroupStatus = 'all' | 'partial' | 'off';

interface GroupSummary {
  counts: Record<VehicleKey, number>;
  activeSlots: number;
  totalSlots: number;
  status: GroupStatus;
}

const GROUPS: CategoryGroup[] = [
  {
    id: 'telephony',
    label: 'Telefonia',
    icon: Phone,
    description: 'Chiamate vocali e messaggi vocali',
    categories: [
      { key: 'incoming_calls', label: 'Chiamate in arrivo', icon: Phone, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: true }, tone: true },
      { key: 'missed_calls', label: 'Chiamate perse', icon: PhoneMissed, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true } },
      { key: 'voicemail', label: 'Messaggi in segreteria', icon: Voicemail, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true }, tone: true },
    ],
  },
  {
    id: 'messaging',
    label: 'Messaggistica',
    icon: MessageSquare,
    description: 'SMS, WhatsApp, chat interna',
    categories: [
      { key: 'sms_inbound', label: 'SMS in arrivo', icon: MessageSquare, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: true } },
      { key: 'whatsapp_inbound', label: 'WhatsApp in arrivo', icon: MessageSquare, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: true } },
      { key: 'telegram_inbound', label: 'Telegram in arrivo', icon: Send, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: false } },
      { key: 'chat_message', label: 'Chat interna - messaggi diretti', icon: MessageCircle, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: true } },
      { key: 'chat_mention', label: 'Chat interna - menzioni', icon: AtSign, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: true } },
    ],
  },
  {
    id: 'email',
    label: 'Email',
    icon: Mail,
    description: 'Email ricevute sui tuoi account collegati',
    categories: [
      { key: 'email_received', label: 'Notifiche per nuove email', icon: Mail, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: true } },
    ],
  },
  {
    id: 'feed',
    label: 'Feed aziendale',
    icon: Newspaper,
    description: 'Annunci, commenti, menzioni e reaction',
    categories: [
      { key: 'feed_post', label: 'Annunci', icon: Newspaper, vehicles: { in_app: true, push: true, email: false, sound: false, telegram: true } },
      { key: 'feed_comment', label: 'Commenti al tuo post', icon: MessageSquare, vehicles: { in_app: true, push: true, email: false, sound: false, telegram: true } },
      { key: 'feed_mention', label: 'Menzioni', icon: AtSign, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: true } },
      { key: 'feed_reaction', label: 'Reaction al tuo contenuto', icon: Zap, vehicles: { in_app: true, push: true, email: false, sound: false, telegram: true } },
      { key: 'feed_report', label: 'Segnalazioni contenuti', icon: ShieldCheck, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: false } },
    ],
  },
  {
    id: 'calendar',
    label: 'Calendario',
    icon: Calendar,
    description: 'Promemoria per appuntamenti, task e video call',
    categories: [
      { key: 'calendar_appointment', label: 'Appuntamenti', icon: Calendar, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true }, reminders: true },
      { key: 'calendar_task', label: 'Task', icon: CheckSquare, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true }, reminders: true },
      { key: 'calendar_videocall', label: 'Video Call', icon: Video, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true }, reminders: true },
      { key: 'video_invitation', label: 'Inviti a video call', icon: Video, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true } },
      { key: 'appointment_reassigned', label: 'Appuntamento riassegnato', icon: Calendar, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: true } },
    ],
  },
  {
    id: 'system',
    label: 'Sistema',
    icon: AlertTriangle,
    description: 'Avvisi tecnici, manutenzione e quota storage',
    categories: [
      { key: 'system_alert', label: 'Avvisi di sistema', icon: AlertTriangle, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true } },
      { key: 'service_alert', label: 'Avvisi di servizio', icon: AlertTriangle, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true } },
      { key: 'storage_alert', label: 'Avvisi storage', icon: HardDrive, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true } },
      { key: 'storage_quota', label: 'Quota storage superata', icon: HardDrive, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: false } },
      { key: 'warning', label: 'Avvisi generici', icon: AlertTriangle, vehicles: { in_app: true, push: true, email: false, sound: false, telegram: true } },
    ],
  },
  {
    id: 'operations',
    label: 'Operatività',
    icon: Workflow,
    description: 'Workflow, fatturazione, HR, CRM',
    categories: [
      { key: 'workflow_notification', label: 'Workflow & automazioni', icon: Workflow, vehicles: { in_app: true, push: true, email: false, sound: false, telegram: true } },
      { key: 'invoice_lifecycle', label: 'Fatturazione / POS', icon: FileText, vehicles: { in_app: true, push: true, email: true, sound: false, telegram: true } },
      { key: 'hr_request', label: 'HR - richieste e approvazioni', icon: Briefcase, vehicles: { in_app: true, push: true, email: true, sound: false, telegram: true } },
      { key: 'task_event', label: 'Task assegnati / eventi', icon: ListTodo, vehicles: { in_app: true, push: true, email: false, sound: false, telegram: true } },
      { key: 'crm_event', label: 'CRM - lead e attività', icon: Users, vehicles: { in_app: true, push: true, email: false, sound: false, telegram: true } },
      { key: 'lead_inbound', label: 'Nuovo lead da form/chatbot', icon: UserPlus, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true } },
      { key: 'support_ticket', label: 'Ticket di supporto', icon: MessageSquare, vehicles: { in_app: true, push: true, email: true, sound: false, telegram: true } },
      { key: 'workgroup_invite', label: 'Inviti gruppo di lavoro', icon: Users, vehicles: { in_app: true, push: true, email: false, sound: false, telegram: true } },
      { key: 'pos_fiscal', label: 'Fiscale POS - quadratura Z e scontrini', icon: FileText, vehicles: { in_app: true, push: true, email: false, sound: true, telegram: true }, permission: 'pos.fiscal.notifications' },
      { key: 'pos_logistics', label: 'Logistica - carichi, inventari, import fornitore, trasferimenti fermi', icon: FileText, vehicles: { in_app: true, push: true, email: false, sound: false, telegram: false } },
    ],
  },
  {
    id: 'admin',
    label: 'Amministrazione',
    icon: Server,
    description: 'Visibili solo agli amministratori',
    categories: [
      { key: 'sip_admin', label: 'Gateway / SIP - avvisi admin', icon: Server, vehicles: { in_app: true, push: true, email: true, sound: true, telegram: true }, adminOnly: true },
    ],
  },
];

const REMINDER_OPTIONS = [
  { value: 0, label: 'Nessuno' },
  { value: 2, label: '2 minuti' },
  { value: 5, label: '5 minuti' },
  { value: 10, label: '10 minuti' },
  { value: 15, label: '15 minuti' },
  { value: 30, label: '30 minuti' },
  { value: 60, label: '1 ora' },
  { value: 120, label: '2 ore' },
  { value: 1440, label: '1 giorno' },
];

const AVAILABLE_TONES = [
  { value: 'default', label: 'Predefinito', file: '/sounds/notification-default.mp3' },
  { value: 'classic', label: 'Classico', file: '/sounds/notification-classic.mp3' },
  { value: 'pulse', label: 'Pulse', file: '/sounds/notification-pulse.mp3' },
  { value: 'chime', label: 'Chime', file: '/sounds/notification-chime.mp3' },
];

interface EmailAccountLite {
  id: string;
  account_type: 'gmail' | 'smtp';
  display_name: string;
  email_address: string;
}

/**
 * Resolve the current preference for a category. Priority:
 *  1. The local draft (user has made unsaved changes).
 *  2. The authoritative backend data (from GET /notifications/preferences,
 *     which already embeds CATEGORY_DEFAULTS for rows without a DB entry).
 *  3. A safe transient fallback for the brief window before data loads
 *     (fail-open: better to show toggles as enabled than to silently mute).
 *
 * NOTE: do NOT add local per-category default overrides here. The backend
 * is the single source of truth for defaults (CATEGORY_DEFAULTS in
 * notification.service.ts). Any local override would diverge again.
 */
function getCurrentPref(
  def: CategoryDef,
  draft: Record<string, CategoryPreference>,
  data?: NotificationPreferencesPayload | null,
): CategoryPreference {
  const existing = draft[def.key];
  if (existing) return existing;
  const fromData = data?.categories.find(c => c.category === def.key);
  if (fromData) return fromData;
  // Transient pre-load fallback — avoid showing everything as disabled before
  // the first backend response arrives.
  return {
    category: def.key,
    in_app_enabled: true,
    push_enabled: true,
    email_enabled: false,
    sound_enabled: true,
    telegram_enabled: false,
    first_reminder_minutes: def.reminders ? 30 : null,
    second_reminder_minutes: null,
    tone: null,
  };
}

// Per-domain live summary derived from the current draft: count of categories
// active on each vehicle + aggregate status pill (all / partial / off). A
// "slot" is one (category, supported-vehicle) pair.
function computeGroupSummary(
  group: CategoryGroup,
  draft: Record<string, CategoryPreference>,
  data?: NotificationPreferencesPayload | null,
): GroupSummary {
  const counts: Record<VehicleKey, number> = { push: 0, in_app: 0, email: 0, sound: 0, telegram: 0 };
  let activeSlots = 0;
  let totalSlots = 0;
  for (const def of group.categories) {
    const current = getCurrentPref(def, draft, data);
    for (const v of VEHICLE_DEFS) {
      if (!def.vehicles[v.vk]) continue;
      totalSlots++;
      if (current[v.ek]) {
        activeSlots++;
        counts[v.vk]++;
      }
    }
  }
  const status: GroupStatus = activeSlots === 0 ? 'off' : activeSlots === totalSlots ? 'all' : 'partial';
  return { counts, activeSlots, totalSlots, status };
}

function isAdminRole(role?: string): boolean {
  return role === 'admin' || role === 'super_admin' || role === 'owner';
}

interface NotificationsSettingsTabProps {
  userRole?: string;
}

export default function NotificationsSettingsTab({ userRole }: NotificationsSettingsTabProps) {
  const { toast } = useToast();
  const { data, loading, error, save, saving } = useNotificationPreferences();
  const { hasPermission } = usePermissions();
  const isAdmin = isAdminRole(userRole);

  const [draft, setDraft] = useState<Record<string, CategoryPreference>>({});
  const [quietDraft, setQuietDraft] = useState<QuietHoursPreference>({
    enabled: false, start_time: null, end_time: null, allow_critical_calls: true,
  });
  const [overrideDraft, setOverrideDraft] = useState<Record<string, boolean>>({});
  const [emailAccounts, setEmailAccounts] = useState<EmailAccountLite[]>([]);
  const [emailAccountsLoaded, setEmailAccountsLoaded] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!data) return;
    const indexed: Record<string, CategoryPreference> = {};
    for (const c of data.categories) indexed[c.category] = c;
    setDraft(indexed);
    setQuietDraft({
      enabled: !!data.quiet_hours.enabled,
      start_time: data.quiet_hours.start_time,
      end_time: data.quiet_hours.end_time,
      allow_critical_calls: data.quiet_hours.allow_critical_calls !== false,
    });
    const overrides: Record<string, boolean> = {};
    for (const o of data.email_account_overrides) overrides[o.email_account_id] = o.enabled;
    setOverrideDraft(overrides);
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<{ data?: EmailAccountLite[] } | EmailAccountLite[]>('/email-accounts/all');
        if (cancelled) return;
        const envelope = res?.data;
        const list = Array.isArray(envelope)
          ? envelope
          : (envelope?.data ?? []);
        setEmailAccounts(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setEmailAccounts([]);
      } finally {
        if (!cancelled) setEmailAccountsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visibleGroups = useMemo(() => {
    return GROUPS.map(g => ({
      ...g,
      categories: g.categories.filter(c =>
        (!c.adminOnly || isAdmin) && (!c.permission || hasPermission(c.permission))
      ),
    })).filter(g => g.categories.length > 0);
  }, [isAdmin, hasPermission]);

  const getCurrent = (def: CategoryDef): CategoryPreference => getCurrentPref(def, draft, data);

  // Live per-domain summaries (closed accordion row), recomputed on every draft
  // change so mini-indicators and status pills stay in sync with the toggles.
  const groupSummaries = useMemo(() => {
    const out: Record<string, GroupSummary> = {};
    for (const group of visibleGroups) out[group.id] = computeGroupSummary(group, draft, data);
    return out;
  }, [visibleGroups, draft, data]);

  const setVehicle = (def: CategoryDef, vehicle: 'in_app_enabled' | 'push_enabled' | 'email_enabled' | 'sound_enabled' | 'telegram_enabled', value: boolean) => {
    const current = getCurrent(def);
    setDraft(prev => ({ ...prev, [def.key]: { ...current, [vehicle]: value } }));
  };

  const setReminder = (def: CategoryDef, slot: 'first_reminder_minutes' | 'second_reminder_minutes', value: number | null) => {
    const current = getCurrent(def);
    setDraft(prev => ({ ...prev, [def.key]: { ...current, [slot]: value } }));
  };

  const setTone = (def: CategoryDef, tone: string) => {
    const current = getCurrent(def);
    setDraft(prev => ({ ...prev, [def.key]: { ...current, tone } }));
  };

  const handleSave = async () => {
    const categories: CategoryUpdate[] = [];
    for (const group of visibleGroups) {
      for (const def of group.categories) {
        const c = draft[def.key];
        if (!c) continue;
        const upd: CategoryUpdate = {
          category: def.key,
          in_app_enabled: c.in_app_enabled,
          push_enabled: c.push_enabled,
          email_enabled: c.email_enabled,
          sound_enabled: c.sound_enabled,
          telegram_enabled: c.telegram_enabled,
        };
        if (def.reminders) {
          upd.first_reminder_minutes = c.first_reminder_minutes ?? null;
          upd.second_reminder_minutes = c.second_reminder_minutes ?? null;
        }
        if (def.tone) {
          upd.tone = c.tone ?? 'default';
        }
        categories.push(upd);
      }
    }
    const overridesPayload: EmailAccountOverride[] = emailAccounts.map(a => ({
      email_account_id: a.id,
      enabled: overrideDraft[a.id] !== false,
    }));
    const ok = await save({ categories, quiet_hours: quietDraft, email_account_overrides: overridesPayload });
    if (ok) {
      toast({ title: 'Preferenze notifiche salvate', description: 'Le tue scelte sono attive su tutti i dispositivi web.' });
    } else {
      toast({ title: 'Errore', description: error || 'Impossibile salvare le preferenze', variant: 'destructive' });
    }
  };

  // Task #1681 — master-toggle per veicolo: applica lo stesso valore a tutte
  // le categorie visibili che supportano quel veicolo. Utile per spegnere
  // rapidamente tutti i push browser senza disattivare l'in-app.
  const masterToggleVehicle = (
    vehicle: 'in_app_enabled' | 'push_enabled' | 'email_enabled' | 'sound_enabled' | 'telegram_enabled',
    value: boolean
  ) => {
    setDraft(prev => {
      const next: Record<string, CategoryPreference> = { ...prev };
      const vehicleKey = vehicle.replace('_enabled', '') as 'in_app' | 'push' | 'email' | 'sound' | 'telegram';
      for (const group of visibleGroups) {
        for (const def of group.categories) {
          if (!def.vehicles[vehicleKey]) continue;
          // Use backend defaults (from data) as the fallback when the draft
          // hasn't been populated for this category yet. This avoids the local
          // vehicles-derived defaults that were previously out of sync with the
          // backend CATEGORY_DEFAULTS.
          const current = next[def.key]
            || data?.categories.find(c => c.category === def.key)
            || {
              category: def.key,
              in_app_enabled: true,
              push_enabled: true,
              email_enabled: false,
              sound_enabled: true,
              telegram_enabled: false,
              first_reminder_minutes: null,
              second_reminder_minutes: null,
              tone: null,
            } as CategoryPreference;
          next[def.key] = { ...current, [vehicle]: value };
        }
      }
      return next;
    });
  };

  // Task #1681 — test notification per canale. Centralizzato qui sotto invece
  // di mostrare un bottone su ogni riga: l'utente sceglie il canale dalla
  // lista e riceve una notifica di prova che passa per il normale gating.
  const [testChannel, setTestChannel] = useState<NotificationChannelKey>('chat');
  const [sendingTest, setSendingTest] = useState(false);
  const sendTestNotification = async () => {
    setSendingTest(true);
    try {
      const res = await apiClient.post<{ delivered?: boolean; suppressed?: boolean; channel: string }>(
        '/notifications/test',
        { channel: testChannel }
      );
      if (res.success) {
        if (res.data?.suppressed) {
          toast({
            title: 'Notifica di test soppressa',
            description: `Il canale "${NOTIFICATION_CHANNELS[testChannel].label}" è disattivato nelle tue preferenze: salva con il veicolo abilitato e riprova.`,
          });
        } else {
          toast({
            title: 'Test inviato',
            description: `Verifica la campanella e — se hai dato il consenso al browser — la notifica desktop "${NOTIFICATION_CHANNELS[testChannel].label}".`,
          });
        }
      } else {
        toast({ title: 'Errore', description: res.error || 'Invio test fallito', variant: 'destructive' });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Invio test fallito';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSendingTest(false);
    }
  };

  // Task #1681 — banner stato permesso browser Notification API. Mostra
  // CTA "Abilita" se default/denied (su denied il browser blocca la
  // riproposizione: rimandiamo alle impostazioni del sito).
  const [permissionState, setPermissionState] = useState<NotificationPermission | 'unsupported'>(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
  });
  const requestBrowserPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    try {
      const result = await Notification.requestPermission();
      setPermissionState(result);
      if (result === 'granted') {
        toast({ title: 'Permesso concesso', description: 'Riceverai le notifiche desktop del browser.' });
      } else if (result === 'denied') {
        toast({
          title: 'Permesso negato',
          description: 'Per riattivare le notifiche apri le impostazioni del sito nel browser (icona lucchetto a sinistra dell\'URL).',
          variant: 'destructive',
        });
      }
    } catch {
      // ignore
    }
  };

  const playTone = (toneValue: string | null | undefined) => {
    const tone = AVAILABLE_TONES.find(t => t.value === (toneValue || 'default'));
    if (!tone) return;
    try {
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = tone.file;
      audioRef.current.play().catch(() => {
        toast({ title: 'Anteprima non disponibile', description: 'Verifica che il file audio sia raggiungibile.', variant: 'destructive' });
      });
    } catch {
      // ignore
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-16">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-destructive">
          <CardContent className="py-3 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      {/* Task #1681 — banner permesso browser Notification API. Visibile solo
          se il browser supporta le notifiche e l'utente non le ha ancora
          autorizzate o le ha negate esplicitamente. */}
      {permissionState !== 'granted' && permissionState !== 'unsupported' && (
        <Card className={permissionState === 'denied' ? 'border-destructive/60' : 'border-amber-400/60'}>
          <CardContent className="py-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-3 flex-1">
              <Bell className={`h-5 w-5 shrink-0 mt-0.5 ${permissionState === 'denied' ? 'text-destructive' : 'text-amber-600'}`} />
              <div className="text-sm">
                {permissionState === 'denied' ? (
                  <>
                    <div className="font-medium">Notifiche desktop bloccate per questo sito</div>
                    <div className="text-muted-foreground">
                      Il browser ha negato il permesso. Apri il lucchetto a sinistra dell'URL → Notifiche → Consenti, poi ricarica la pagina.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium">Abilita le notifiche desktop</div>
                    <div className="text-muted-foreground">
                      Per ricevere chiamate, messaggi e avvisi quando la scheda è in background concedi il permesso al browser.
                    </div>
                  </>
                )}
              </div>
            </div>
            {permissionState !== 'denied' && (
              <Button onClick={requestBrowserPermission} size="sm" data-testid="btn-request-notification-permission">
                Abilita notifiche
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Console notifiche
          </CardTitle>
          <CardDescription>
            Attiva o disattiva i veicoli (Push, In-app, Email, Suono, Telegram) per ciascuna categoria.
            Le preferenze sono salvate sul server e applicate a tutti i dispositivi web (browser e PWA installata).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Task #1681 — master-toggle per veicolo: una riga di switch in cima
              che applica lo stesso valore (ON/OFF) a tutte le categorie che
              supportano quel veicolo. Pensata per il "modalità riunione: tutti
              i push off" in 1 click. */}
          <div className="rounded-xl border bg-muted/30 px-4 py-3.5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Master toggle per veicolo</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {VEHICLE_DEFS.map(v => {
                const VIcon = v.icon;
                return (
                  <div key={v.ek} className="flex items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2">
                    <span className="flex items-center gap-2 text-xs font-medium">
                      <VIcon className="h-3.5 w-3.5 text-muted-foreground" />
                      {v.label}
                    </span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 px-2.5 text-[11px]" onClick={() => masterToggleVehicle(v.ek, true)} data-testid={`master-on-${v.ek}`}>ON</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2.5 text-[11px]" onClick={() => masterToggleVehicle(v.ek, false)} data-testid={`master-off-${v.ek}`}>OFF</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Task #2865 — domini ad accordion: tutti chiusi di default, multipli
              apribili insieme. La riga chiusa è un riepilogo scansionabile
              (icona dominio + nome/descrizione + mini-indicatori per veicolo +
              pill di stato aggregato), il pannello espanso è la matrice attuale
              di toggle/promemoria/suoneria, identica nel comportamento. */}
          <Accordion type="multiple" className="space-y-2.5">
            {visibleGroups.map(group => {
              const summary = groupSummaries[group.id];
              const GroupIcon = group.icon;
              return (
                <AccordionItem
                  key={group.id}
                  value={group.id}
                  className="rounded-xl border bg-card border-b overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 gap-3 hover:no-underline hover:bg-accent/30 data-[state=open]:bg-accent/20 transition-colors">
                    <div className="flex flex-1 items-center gap-3 min-w-0">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <GroupIcon className="h-5 w-5 text-muted-foreground" />
                      </span>
                      <div className="min-w-0 text-left">
                        <div className="text-sm font-semibold leading-tight">{group.label}</div>
                        {group.description && <div className="text-xs text-muted-foreground truncate">{group.description}</div>}
                      </div>
                      <div className="ml-auto flex items-center gap-2 sm:gap-3 pl-2">
                        <div className="hidden sm:flex items-center gap-1.5">
                          {VEHICLE_DEFS.map(v => {
                            const count = summary?.counts[v.vk] ?? 0;
                            const on = count > 0;
                            const VIcon = v.icon;
                            return (
                              <span
                                key={v.vk}
                                title={`${v.label}: ${count} categori${count === 1 ? 'a' : 'e'} attiv${count === 1 ? 'a' : 'e'}`}
                                className={cn(
                                  'flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] tabular-nums transition-colors',
                                  on ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground/40'
                                )}
                              >
                                <VIcon className="h-3 w-3" />
                                {count}
                              </span>
                            );
                          })}
                        </div>
                        <StatusPill status={summary?.status ?? 'off'} />
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-0 pb-0">
                    <div className="border-t px-4 pt-4 pb-4 space-y-2">
                      <div className="hidden md:grid md:grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-muted-foreground px-3">
                        <div className="col-span-7">Categoria</div>
                        <div className="col-span-1 text-center">Push</div>
                        <div className="col-span-1 text-center">In-app</div>
                        <div className="col-span-1 text-center">Email</div>
                        <div className="col-span-1 text-center">Suono</div>
                        <div className="col-span-1 text-center flex items-center justify-center gap-1">
                          <Send className="h-3 w-3" /> Telegram
                        </div>
                      </div>

                      <div className="space-y-2">
                        {group.categories.map(def => {
                          const current = getCurrent(def);
                          const Icon = def.icon;
                          return (
                            <div
                              key={def.key}
                              data-testid={`pref-row-${def.key}`}
                              className="rounded-lg border bg-card hover:bg-accent/30 transition-colors"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center px-3 py-3">
                                <div className="md:col-span-7 flex items-center gap-3">
                                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                                  <div>
                                    <div className="text-sm font-medium">{def.label}</div>
                                    {def.description && <div className="text-xs text-muted-foreground">{def.description}</div>}
                                  </div>
                                </div>
                                <VehicleCell
                                  enabled={def.vehicles.push}
                                  checked={current.push_enabled}
                                  onChange={(v) => setVehicle(def, 'push_enabled', v)}
                                  srLabel="Push"
                                />
                                <VehicleCell
                                  enabled={def.vehicles.in_app}
                                  checked={current.in_app_enabled}
                                  onChange={(v) => setVehicle(def, 'in_app_enabled', v)}
                                  srLabel="In-app"
                                />
                                <VehicleCell
                                  enabled={def.vehicles.email}
                                  checked={current.email_enabled}
                                  onChange={(v) => setVehicle(def, 'email_enabled', v)}
                                  srLabel="Email"
                                />
                                <VehicleCell
                                  enabled={def.vehicles.sound}
                                  checked={current.sound_enabled}
                                  onChange={(v) => setVehicle(def, 'sound_enabled', v)}
                                  srLabel="Suono"
                                />
                                <VehicleCell
                                  enabled={def.vehicles.telegram}
                                  checked={current.telegram_enabled}
                                  onChange={(v) => setVehicle(def, 'telegram_enabled', v)}
                                  srLabel="Telegram"
                                />
                              </div>

                              {def.reminders && current.in_app_enabled && (
                                <div className="border-t bg-muted/30 px-3 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Primo promemoria</Label>
                                    <ReminderSelect
                                      value={current.first_reminder_minutes ?? 0}
                                      onChange={(v) => setReminder(def, 'first_reminder_minutes', v === 0 ? null : v)}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Secondo promemoria</Label>
                                    <ReminderSelect
                                      value={current.second_reminder_minutes ?? 0}
                                      onChange={(v) => setReminder(def, 'second_reminder_minutes', v === 0 ? null : v)}
                                    />
                                  </div>
                                </div>
                              )}

                              {def.tone && current.sound_enabled && (
                                <div className="border-t bg-muted/30 px-3 py-3 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
                                  <div className="sm:col-span-4">
                                    <Label className="text-xs">Suoneria</Label>
                                  </div>
                                  <div className="sm:col-span-6">
                                    <Select value={current.tone || 'default'} onValueChange={(v) => setTone(def, v)}>
                                      <SelectTrigger className="h-9 text-sm" data-testid={`tone-select-${def.key}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {AVAILABLE_TONES.map(t => (
                                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="sm:col-span-2">
                                    <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => playTone(current.tone)}>
                                      <Play className="h-3.5 w-3.5 mr-1" /> Anteprima
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Account email collegati</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Disattiva le notifiche solo per alcuni account email. Le scelte sono salvate sul server e sincronizzate fra dispositivi.
              </p>
            </div>
            {!emailAccountsLoaded ? (
              <div className="text-sm text-muted-foreground">Caricamento account…</div>
            ) : emailAccounts.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-sm">
                Nessun account email collegato.{' '}
                <Link to="/email-accounts" className="text-primary underline">Collega un account email</Link>
                {' '}per iniziare a ricevere notifiche.
              </div>
            ) : (
              <div className="space-y-2">
                {emailAccounts.map(a => (
                  <div key={a.id} className="flex items-center justify-between rounded-md border bg-card px-3 py-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{a.display_name || a.email_address}</div>
                        <div className="text-xs text-muted-foreground truncate">{a.email_address} · {a.account_type === 'gmail' ? 'Gmail' : 'SMTP'}</div>
                      </div>
                    </div>
                    <Switch
                      checked={overrideDraft[a.id] !== false}
                      onCheckedChange={(v) => setOverrideDraft(prev => ({ ...prev, [a.id]: v }))}
                      data-testid={`email-account-toggle-${a.id}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Task #1681 — Invia notifica di test per canale + mini-preview
              (il preview mostra come apparirà la notifica desktop con titolo
              prefisso, icona/badge derivati dalla SoT shared). */}
          <section className="space-y-3 rounded-md border bg-card p-3">
            <div className="flex items-center gap-2 font-medium text-sm">
              <Send className="h-4 w-4" /> Invia una notifica di test
            </div>
            <p className="text-xs text-muted-foreground">
              Verifica end-to-end che il canale scelto consegni push browser + suono + deep-link. Rispetta le tue preferenze: se il push è OFF per quel canale, il test risponderà "soppresso".
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-5">
                <Label className="text-xs">Canale</Label>
                <Select value={testChannel} onValueChange={(v) => setTestChannel(v as NotificationChannelKey)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="test-channel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(NOTIFICATION_CHANNELS) as NotificationChannelKey[]).map(k => (
                      <SelectItem key={k} value={k}>{NOTIFICATION_CHANNELS[k].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-3">
                <Button onClick={sendTestNotification} disabled={sendingTest} className="w-full" data-testid="btn-send-test-notification">
                  <Send className="h-3.5 w-3.5 mr-1" /> {sendingTest ? 'Invio…' : 'Invia test'}
                </Button>
              </div>
              <div className="sm:col-span-4">
                {/* Mini-preview: replica del banner desktop in stile macOS/Win */}
                <div className="rounded-md border bg-background shadow-sm px-3 py-2 flex items-center gap-2" data-testid="test-channel-preview">
                  <img
                    src={NOTIFICATION_CHANNELS[testChannel].icon}
                    alt=""
                    className="h-8 w-8 rounded"
                    onError={(e) => {
                      // Graceful fallback to the existing EasyFlow icon if a
                      // channel-specific asset is ever missing (no 404 loop).
                      const img = e.currentTarget as HTMLImageElement;
                      if (img.src.endsWith(FALLBACK_NOTIFICATION_ICON)) return;
                      img.src = FALLBACK_NOTIFICATION_ICON;
                    }}
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate flex items-center gap-1">
                      <Monitor className="h-3 w-3 text-muted-foreground" />
                      {NOTIFICATION_CHANNELS[testChannel].titlePrefix || 'EasyFlow'}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">Notifica di test ({NOTIFICATION_CHANNELS[testChannel].label})</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Smartphone className="h-3 w-3" /> Su mobile la notifica appare nel center notifiche del sistema operativo se hai installato la PWA.
            </div>
          </section>

          <section className="space-y-2 rounded-md border bg-muted/40 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium">
              <MessageCircle className="h-4 w-4" /> Override per-conversazione
            </div>
            <p className="text-muted-foreground text-xs">
              Per la chat interna puoi sovrascrivere le notifiche di una singola conversazione (tutti i messaggi /
              solo menzioni / nessuna). Apri la conversazione in <Link to="/messenger" className="text-primary underline">Messenger</Link>{' '}
              e usa il menu impostazioni della chat.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Moon className="h-5 w-5" /> Quiet hours / Non disturbare
          </CardTitle>
          <CardDescription>
            Silenzia push e suoni durante una fascia oraria. Le notifiche restano comunque visibili nella campanella e nello storico.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Attiva Non disturbare</Label>
              <p className="text-xs text-muted-foreground">Quando attivo, push e suoni sono silenziati nella fascia indicata.</p>
            </div>
            <Switch
              checked={quietDraft.enabled}
              onCheckedChange={(v) => setQuietDraft(q => ({ ...q, enabled: v }))}
              data-testid="quiet-hours-toggle"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Inizio</Label>
              <Input
                type="time"
                value={quietDraft.start_time || ''}
                onChange={(e) => setQuietDraft(q => ({ ...q, start_time: e.target.value || null }))}
                disabled={!quietDraft.enabled}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Fine</Label>
              <Input
                type="time"
                value={quietDraft.end_time || ''}
                onChange={(e) => setQuietDraft(q => ({ ...q, end_time: e.target.value || null }))}
                disabled={!quietDraft.enabled}
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-3">
            <div className="space-y-0.5">
              <Label className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                Consenti chiamate critiche
              </Label>
              <p className="text-xs text-muted-foreground">
                Quando attivo, le chiamate in arrivo restano udibili anche durante la fascia di Non disturbare.
              </p>
            </div>
            <Switch
              checked={quietDraft.allow_critical_calls}
              onCheckedChange={(v) => setQuietDraft(q => ({ ...q, allow_critical_calls: v }))}
              disabled={!quietDraft.enabled}
              data-testid="quiet-hours-allow-critical"
            />
          </div>
          {quietDraft.enabled && !quietDraft.allow_critical_calls && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
              <Badge variant="outline" className="mr-2 border-amber-400 text-amber-800 dark:text-amber-200">Attenzione</Badge>
              Anche le chiamate in arrivo verranno silenziate durante la fascia indicata.
            </div>
          )}
        </CardContent>
      </Card>

      <TelegramPairingCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="h-5 w-5" /> Suoneria notifiche
          </CardTitle>
          <CardDescription>
            La suoneria è configurabile per categoria nelle righe della console qui sopra (es. Chiamate in arrivo, Segreteria).
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Apri le righe con sezione <em>Suoneria</em> nella matrice per scegliere e ascoltare l’anteprima del tono.
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg" data-testid="save-notification-prefs">
          {saving ? (
            <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Salvataggio…</>
          ) : (
            <><Save className="h-4 w-4 mr-2" /> Salva preferenze</>
          )}
        </Button>
      </div>
    </div>
  );
}

const STATUS_PILL_CONFIG: Record<GroupStatus, { label: string; className: string }> = {
  all: { label: 'Tutto attivo', className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' },
  partial: { label: 'Parziale', className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30' },
  off: { label: 'Spento', className: 'bg-muted text-muted-foreground border-transparent' },
};

function StatusPill({ status }: { status: GroupStatus }) {
  const cfg = STATUS_PILL_CONFIG[status];
  return (
    <span className={cn('shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap', cfg.className)}>
      {cfg.label}
    </span>
  );
}

interface VehicleCellProps {
  enabled: boolean;
  checked: boolean;
  onChange: (v: boolean) => void;
  srLabel: string;
  colSpan?: number;
}

function VehicleCell({ enabled, checked, onChange, srLabel, colSpan = 1 }: VehicleCellProps) {
  const colClass = colSpan === 2 ? 'md:col-span-2' : 'md:col-span-1';
  return (
    <div className={`flex items-center md:justify-center gap-2 ${colClass}`}>
      <Switch
        checked={enabled && checked}
        onCheckedChange={onChange}
        disabled={!enabled}
        aria-label={srLabel}
      />
      <span className="md:hidden text-xs text-muted-foreground">{srLabel}</span>
    </div>
  );
}

interface ReminderSelectProps {
  value: number;
  onChange: (v: number) => void;
}

function ReminderSelect({ value, onChange }: ReminderSelectProps) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="h-9 text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {REMINDER_OPTIONS.map(opt => (
          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
